import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { capitalizeFirst } from '../../utils';

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  Settings,
  RefreshCw,
  Clock,
  Eye,
  EyeOff,
  Maximize2,
  Minimize2,
  Info,
} from 'lucide-react';
import {
  preventInvalidNumberInput,
  preventInvalidNumberPaste,
} from '../../utils/validationMessages';

interface MarketData {
  symbol: string;
  bid: number;
  ask: number;
  spread: number;
  change: number;
  changePercent: number;
  high: number;
  low: number;
  volume: string;
}

interface Position {
  id: string;
  symbol: string;
  type: 'buy' | 'sell';
  volume: number;
  openPrice: number;
  currentPrice: number;
  stopLoss?: number;
  takeProfit?: number;
  profit: number;
  profitPercent: number;
  swap: number;
  commission: number;
  openTime: string;
  status: 'active' | 'pending' | 'closed';
}

interface Order {
  id: string;
  symbol: string;
  type: 'buy' | 'sell' | 'buy_limit' | 'sell_limit' | 'buy_stop' | 'sell_stop';
  volume: number;
  price: number;
  stopLoss?: number;
  takeProfit?: number;
  status: 'pending' | 'executed' | 'cancelled';
  createTime: string;
}

const WebTerminal = () => {
  const [selectedSymbol, setSelectedSymbol] = useState('EUR/USD');
  const [chartTimeframe, setChartTimeframe] = useState('1H');
  const [showPositions, setShowPositions] = useState(true);
  const [showOrders, setShowOrders] = useState(true);
  const [chartFullscreen, setChartFullscreen] = useState(false);
  const [volume, setVolume] = useState(0.01);
  const [stopLoss, setStopLoss] = useState('');
  const [takeProfit, setTakeProfit] = useState('');

  const marketData: MarketData[] = [
    {
      symbol: 'EUR/USD',
      bid: 1.0875,
      ask: 1.0878,
      spread: 3,
      change: 0.0025,
      changePercent: 0.23,
      high: 1.089,
      low: 1.085,
      volume: '125.3M',
    },
    {
      symbol: 'GBP/USD',
      bid: 1.2725,
      ask: 1.2728,
      spread: 3,
      change: -0.0018,
      changePercent: -0.14,
      high: 1.275,
      low: 1.27,
      volume: '89.7M',
    },
    {
      symbol: 'USD/JPY',
      bid: 148.5,
      ask: 148.53,
      spread: 3,
      change: 0.15,
      changePercent: 0.1,
      high: 148.75,
      low: 148.25,
      volume: '156.2M',
    },
    {
      symbol: 'AUD/USD',
      bid: 0.655,
      ask: 0.6553,
      spread: 3,
      change: 0.0012,
      changePercent: 0.18,
      high: 0.6575,
      low: 0.6525,
      volume: '67.8M',
    },
  ];

  const positions: Position[] = [
    {
      id: 'POS-001',
      symbol: 'EUR/USD',
      type: 'buy',
      volume: 0.1,
      openPrice: 1.085,
      currentPrice: 1.0875,
      stopLoss: 1.082,
      takeProfit: 1.092,
      profit: 25.0,
      profitPercent: 2.31,
      swap: -0.15,
      commission: 0.5,
      openTime: '2024-03-18 10:30',
      status: 'active',
    },
    {
      id: 'POS-002',
      symbol: 'GBP/USD',
      type: 'sell',
      volume: 0.05,
      openPrice: 1.275,
      currentPrice: 1.2725,
      stopLoss: 1.278,
      takeProfit: 1.268,
      profit: 12.5,
      profitPercent: 1.96,
      swap: 0.1,
      commission: 0.25,
      openTime: '2024-03-18 09:15',
      status: 'active',
    },
  ];

  const orders: Order[] = [
    {
      id: 'ORD-001',
      symbol: 'USD/JPY',
      type: 'buy_limit',
      volume: 0.15,
      price: 148.25,
      stopLoss: 147.8,
      takeProfit: 149.0,
      status: 'pending',
      createTime: '2024-03-18 11:45',
    },
  ];

  const currentMarketData =
    marketData.find(m => m.symbol === selectedSymbol) || marketData[0];

  const formatTime = (time: string) => {
    return new Date(time).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  return (
    <div className="h-screen bg-background flex flex-col">
      <div className="border-b bg-card sticky top-0 z-20">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-bold">Web Terminal</h1>
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span>{new Date().toLocaleTimeString()}</span>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </Button>
            <Button variant="outline" size="sm">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div className="w-80 border-r bg-card flex flex-col">
          <div className="p-2 border-b">
            <h2 className="font-semibold mb-3">Market Watch</h2>
            <div className="space-y-1">
              {marketData.map(market => (
                <div
                  key={market.symbol}
                  className={`rounded cursor-pointer transition-colors ${
                    selectedSymbol === market.symbol
                      ? 'bg-accent'
                      : 'hover:bg-accent/50'
                  }`}
                  onClick={() => setSelectedSymbol(market.symbol)}
                >
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-sm">{market.symbol}</span>
                    <div className="text-right">
                      <div className="text-sm font-mono">
                        {market.bid.toFixed(4)}
                      </div>
                      <div
                        className={`text-xs flex items-center ${
                          market.change >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {market.change >= 0 ? (
                          <ArrowUpRight className="h-3 w-3" />
                        ) : (
                          <ArrowDownRight className="h-3 w-3" />
                        )}
                        {market.changePercent.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-xs text-muted-foreground mt-1">
                    <span>{market.ask.toFixed(4)}</span>
                    <span>Spread: {market.spread}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="p-2 border-b">
            <h3 className="font-semibold mb-3">Account</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Balance:</span>
                <span className="font-medium">$10,250.00</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Equity:</span>
                <span className="font-medium">$10,287.50</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Margin:</span>
                <span className="font-medium">$1,250.00</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Free Margin:</span>
                <span className="font-medium">$9,037.50</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Margin Level:</span>
                <span className="font-medium text-green-600">822.2%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col">
          <div
            className={`flex-1 ${chartFullscreen ? 'fixed inset-0 z-50 bg-background' : ''}`}
          >
            <Card className="h-full">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <CardTitle className="text-lg">{selectedSymbol}</CardTitle>
                    <div className="flex items-center space-x-2">
                      <Badge
                        variant={
                          currentMarketData.change >= 0
                            ? 'default'
                            : 'destructive'
                        }
                      >
                        {currentMarketData.bid.toFixed(4)}
                      </Badge>
                      <span className="text-sm text-muted-foreground">
                        {currentMarketData.ask.toFixed(4)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="flex space-x-1">
                      {['1M', '5M', '15M', '1H', '4H', '1D', '1W'].map(tf => (
                        <Button
                          key={tf}
                          variant={
                            chartTimeframe === tf ? 'default' : 'outline'
                          }
                          size="sm"
                          onClick={() => setChartTimeframe(tf)}
                        >
                          {tf}
                        </Button>
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setChartFullscreen(!chartFullscreen)}
                    >
                      {chartFullscreen ? (
                        <Minimize2 className="h-4 w-4" />
                      ) : (
                        <Maximize2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 h-full">
                <div className="h-full bg-gradient-to-b from-background to-muted/20 flex items-center justify-center">
                  <div className="text-center">
                    <BarChart3 className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">Chart View</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      {selectedSymbol} - {chartTimeframe} timeframe
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="border-t bg-card">
            <Tabs defaultValue="trade" className="h-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="trade">Trade</TabsTrigger>
                <TabsTrigger value="positions">Positions</TabsTrigger>
                <TabsTrigger value="orders">Orders</TabsTrigger>
              </TabsList>

              <TabsContent value="trade" className="p-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-2">
                      <Button className="bg-green-600 hover:bg-green-700">
                        <ArrowUpRight className="h-4 w-4 mr-2" />
                        Buy {selectedSymbol}
                      </Button>
                      <Button variant="destructive">
                        <ArrowDownRight className="h-4 w-4 mr-2" />
                        Sell {selectedSymbol}
                      </Button>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="volume">Volume (lots)</Label>
                        <Input
                          id="volume"
                          type="number"
                          value={volume}
                          onKeyDown={preventInvalidNumberInput}
                          onPaste={preventInvalidNumberPaste}
                          onChange={e =>
                            setVolume(parseFloat(e.target.value) || 0)
                          }
                          step="0.01"
                          min="0.01"
                          max="100"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label htmlFor="stopLoss">Stop Loss</Label>
                          <Input
                            id="stopLoss"
                            type="number"
                            value={stopLoss}
                            onKeyDown={preventInvalidNumberInput}
                            onPaste={preventInvalidNumberPaste}
                            onChange={e => setStopLoss(e.target.value)}
                            placeholder="Optional"
                          />
                        </div>
                        <div>
                          <Label htmlFor="takeProfit">Take Profit</Label>
                          <Input
                            id="takeProfit"
                            type="number"
                            value={takeProfit}
                            onKeyDown={preventInvalidNumberInput}
                            onPaste={preventInvalidNumberPaste}
                            onChange={e => setTakeProfit(e.target.value)}
                            placeholder="Optional"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Required Margin:
                          </span>
                          <span>${(volume * 1000).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">
                            Spread Cost:
                          </span>
                          <span>
                            ${(currentMarketData.spread * volume).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">High:</span>
                        <div className="font-medium">
                          {currentMarketData.high.toFixed(4)}
                        </div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Low:</span>
                        <div className="font-medium">
                          {currentMarketData.low.toFixed(4)}
                        </div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Spread:</span>
                        <div className="font-medium">
                          {currentMarketData.spread} pips
                        </div>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Volume:</span>
                        <div className="font-medium">
                          {currentMarketData.volume}
                        </div>
                      </div>
                    </div>

                    <div className="p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <Info className="h-4 w-4 text-blue-500" />
                        <span className="text-sm font-medium">
                          Market Information
                        </span>
                      </div>
                      <div className="text-xs space-y-1 text-muted-foreground">
                        <div>Trading Hours: 24/5</div>
                        <div>
                          Server Time: {new Date().toLocaleTimeString()}
                        </div>
                        <div>Swap Time: 00:00 GMT</div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="positions" className="p-4">
                <div className="space-y-3">
                  {positions.map(position => (
                    <div key={position.id} className="border rounded-lg p-3">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">
                              {position.symbol}
                            </span>
                            <Badge
                              variant={
                                position.type === 'buy'
                                  ? 'default'
                                  : 'destructive'
                              }
                            >
                              {capitalizeFirst(position.type)}
                            </Badge>
                            <Badge variant="outline">
                              {position.volume} lots
                            </Badge>
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            #{position.id} • Opened{' '}
                            {formatTime(position.openTime)}
                          </div>
                        </div>
                        <div className="text-right">
                          <div
                            className={`font-bold ${
                              position.profit >= 0
                                ? 'text-green-600'
                                : 'text-red-600'
                            }`}
                          >
                            ${position.profit.toFixed(2)}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {position.profitPercent.toFixed(2)}%
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-4 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Open:</span>
                          <div className="font-mono">
                            {position.openPrice.toFixed(4)}
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            Current:
                          </span>
                          <div className="font-mono">
                            {position.currentPrice.toFixed(4)}
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">S/L:</span>
                          <div className="font-mono">
                            {position.stopLoss?.toFixed(4) || '-'}
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">T/P:</span>
                          <div className="font-mono">
                            {position.takeProfit?.toFixed(4) || '-'}
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-2 mt-3">
                        <Button variant="outline" size="sm">
                          Close
                        </Button>
                        <Button variant="outline" size="sm">
                          Modify
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="orders" className="p-4">
                <div className="space-y-3">
                  {orders.map(order => (
                    <div key={order.id} className="border rounded-lg p-3">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">{order.symbol}</span>
                            <Badge variant="outline">
                              {order.type.replace('_', ' ').toUpperCase()}
                            </Badge>
                            <Badge variant="outline">{order.volume} lots</Badge>
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            #{order.id} • {formatTime(order.createTime)}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge
                            variant={
                              order.status === 'pending'
                                ? 'secondary'
                                : 'default'
                            }
                          >
                            {order.status}
                          </Badge>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Price:</span>
                          <div className="font-mono">
                            {order.price.toFixed(4)}
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">S/L:</span>
                          <div className="font-mono">
                            {order.stopLoss?.toFixed(4) || '-'}
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">T/P:</span>
                          <div className="font-mono">
                            {order.takeProfit?.toFixed(4) || '-'}
                          </div>
                        </div>
                      </div>

                      <div className="flex space-x-2 mt-3">
                        <Button variant="outline" size="sm">
                          Cancel
                        </Button>
                        <Button variant="outline" size="sm">
                          Modify
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <div className="w-80 border-l bg-card">
          <div className="flex flex-col h-full">
            <div className="flex-1 border-b">
              <div className="p-1 border-b flex items-center justify-between">
                <h3 className="font-semibold">Positions</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPositions(!showPositions)}
                >
                  {showPositions ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {showPositions && (
                <div className="p-4 space-y-3">
                  {positions.map(position => (
                    <div key={position.id} className="border rounded p-2">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-sm">
                          {position.symbol}
                        </span>
                        <Badge
                          variant={
                            position.type === 'buy' ? 'default' : 'destructive'
                          }
                          className="text-xs"
                        >
                          {position.type}
                        </Badge>
                      </div>
                      <div className="text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Volume:</span>
                          <span>{position.volume}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">P&L:</span>
                          <span
                            className={
                              position.profit >= 0
                                ? 'text-green-600'
                                : 'text-red-600'
                            }
                          >
                            ${position.profit.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex-1">
              <div className="p-1 border-b flex items-center justify-between">
                <h3 className="font-semibold">Orders</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowOrders(!showOrders)}
                >
                  {showOrders ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {showOrders && (
                <div className="p-4 space-y-3">
                  {orders.map(order => (
                    <div key={order.id} className="border rounded p-2">
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-medium text-sm">
                          {order.symbol}
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          {order.type.replace('_', ' ')}
                        </Badge>
                      </div>
                      <div className="text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Price:</span>
                          <span>{order.price.toFixed(4)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Volume:</span>
                          <span>{order.volume}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebTerminal;
